import { useState } from "react";
import { toast } from "sonner";
import { useAdminApplications, useAdminProgrammes, adminApi, useAdminMutation, errorMessage, fmt, inputCls } from "@/hooks/useAdmin";
import { AdminHeading, Badge } from "@/components/admin/AdminLayout";

const STATUSES = ["", "submitted", "under_review", "waitlisted", "selected", "accepted", "declined", "withdrawn", "cancelled", "completed"];
const tone = { submitted: "sage", under_review: "champagne", waitlisted: "champagne", selected: "forest", accepted: "forest", completed: "sage", cancelled: "red" };
const actions = [
  { status: "under_review", label: "Mark under review", cls: "btn-outline" },
  { status: "waitlisted", label: "Waitlist", cls: "btn-outline" },
  { status: "selected", label: "Select & invite", cls: "btn-primary" },
  { status: "cancelled", label: "Cancel", cls: "btn-outline" },
];

function Row({ app }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const setStatus = useAdminMutation(adminApi.setApplicationStatus, (d) => toast.success(`Application ${d.status.replace("_", " ")}.`));
  const actionable = ["submitted", "under_review", "waitlisted"].includes(app.status);
  const member = app.member || {};
  return (
    <div className="border border-forest/15 bg-cream" data-testid={`admin-application-${app.application_id}`} data-status={app.status}>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left p-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-center" data-testid={`admin-application-toggle-${app.application_id}`}>
        <div>
          <p className="font-serif text-xl text-forest">{member.display_name || member.name || "Member"}</p>
          <p className="text-xs text-charcoal/55">{member.email}</p>
        </div>
        <div>
          <p className="text-sm text-charcoal/75">{app.programme_title}</p>
          <p className="text-xs text-charcoal/45">Applied {fmt(app.created_at)}{app.trial && ` · trial ${app.trial.status.replace("_", " ")}`}</p>
        </div>
        <Badge tone={tone[app.status]}>{app.status.replace("_", " ")}</Badge>
      </button>
      {open && (
        <div className="border-t border-forest/10 p-5 grid lg:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Answers</p>
            <dl className="mt-3 space-y-3 text-sm">
              {Object.entries(app.answers || {}).map(([k, v]) => <div key={k}><dt className="text-charcoal/50">{k.replace("_", " ")}</dt><dd className="text-charcoal/85">{String(v) || "—"}</dd></div>)}
            </dl>
            {app.shipping && (
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Shipping</p>
                <p className="mt-2 text-sm text-charcoal/80">{[app.shipping.full_name, app.shipping.line1, app.shipping.line2, app.shipping.city, app.shipping.postcode, app.shipping.country].filter(Boolean).join(", ")}</p>
              </div>
            )}
            <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-champagne">History</p>
            <ol className="mt-2 space-y-1 text-xs text-charcoal/60">{app.timeline.map((t) => <li key={`${t.at}-${t.status}`}>{fmt(t.at)} — {t.status.replace("_", " ")} ({t.by}){t.note && `: ${t.note}`}</li>)}</ol>
          </div>
          <div>
            {actionable ? (
              <>
                <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Decision</p>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (recorded in history)" className={`mt-3 ${inputCls}`} data-testid={`decision-note-${app.application_id}`} />
                <div className="mt-3 flex flex-wrap gap-2">
                  {actions.filter((a) => a.status !== app.status).map((a) => (
                    <button key={a.status} onClick={() => setStatus.mutate({ id: app.application_id, status: a.status, note: note || null }, { onError: (e) => toast.error(errorMessage(e)) })} className={`btn ${a.cls} !px-4 !py-2.5`} data-testid={`decision-${a.status}-${app.application_id}`}>{a.label}</button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-charcoal/45 font-light">Selecting issues an invitation the member must accept in My Rituals. Emails are not yet sent (dev-preview mode).</p>
              </>
            ) : <p className="text-sm text-charcoal/55 font-light">No decision available in state "{app.status.replace("_", " ")}".</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminApplicants() {
  const [status, setStatus] = useState("");
  const [programmeId, setProgrammeId] = useState("");
  const { data: apps = [] } = useAdminApplications({ status, programme_id: programmeId });
  const { data: programmes = [] } = useAdminProgrammes();
  return (
    <div data-testid="admin-applicants">
      <AdminHeading eyebrow="Review & select" title="Applicants" />
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={programmeId} onChange={(e) => setProgrammeId(e.target.value)} className={`${inputCls} !w-auto`} data-testid="filter-programme"><option value="">All programmes</option>{programmes.map((p) => <option key={p.programme_id} value={p.programme_id}>{p.title}</option>)}</select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-auto`} data-testid="filter-status">{STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace("_", " ") : "All statuses"}</option>)}</select>
        <span className="self-center text-xs text-charcoal/50">{apps.length} application{apps.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-3">
        {apps.length === 0 && <p className="text-sm text-charcoal/50 font-light border border-forest/10 p-8 text-center" data-testid="applicants-empty">No applications match.</p>}
        {apps.map((a) => <Row key={a.application_id} app={a} />)}
      </div>
    </div>
  );
}
